import { Auth } from 'aws-amplify'
import config from '~/config'
import {
  createAuthTokenResolver,
  createReportThrottle,
  markTokenUnavailable,
  reportTokenUnavailable,
  reportTokenRecovered
} from '~/helpers/axiosAuth'

export default (context) => {
  const { $axios, redirect, store, app } = context
  // @nuxtjs/sentry が注入する $sentry(無効化環境ではモック)
  const sentry = (app && app.$sentry) || context.$sentry
  // 画面のパスはリクエスト時点のものを使う(context.route は画面遷移のたびに更新される。
  // プラグイン初期化時に取り出すと初期画面のパスのまま固定されてしまう)
  const currentPath = () => {
    const current =
      context.route || (app && app.router && app.router.currentRoute)
    return current && current.fullPath
  }
  // Sentry 通知の間引き(種類ごとに 1 分に 1 回まで)
  const shouldReport = createReportThrottle()
  $axios.onResponseError(async (error) => {
    if (error.response && error.response.status === 401) {
      // トークンが一時的に取れずにヘッダー無しで送ったリクエストの 401 は、セッション切れではないので
      // サインアウトもリダイレクトもせず、呼び出し元のエラー処理(トースト等)に任せる
      if (error.config && error.config.tokenUnavailable) {
        throw markTokenUnavailable(error)
      }
      store.commit('setAccessToken', undefined)
      // ログイン後の戻り先。パスが取れないときは保存しない(文字列 "undefined" を保存しないため。ログイン側は既定の遷移先に進む)
      const path = currentPath()
      if (path) {
        localStorage.setItem(config.redirectKey, path)
      }
      await Auth.signOut()
      redirect('/login')
      return
    }
    throw error
  })

  const resolveToken = createAuthTokenResolver({
    getToken: async () => {
      const session = await Auth.currentSession()
      return session.getIdToken().getJwtToken()
    }
  })

  $axios.onRequest(async (request) => {
    const result = await resolveToken()

    if (result.status === 'ok') {
      request.headers.common['X-Authorization'] = result.token
      // 再試行の末に取れたときは、発生頻度を見るため Sentry に info で送る。
      // 同時に走ったリクエストは 1 回の取得結果を共有するので、通知は結果ごとに 1 回だけ(最初のリクエストの情報を付ける)
      if (result.retried && !result.reported) {
        result.reported = true
        shouldReport('recovered') &&
          reportTokenRecovered({
            sentry,
            error: result.error,
            path: currentPath(),
            method: request.method,
            url: request.url
          })
      }
      return request
    }

    if (result.status === 'unavailable') {
      // 一時的にトークンが取れない(リフレッシュ通信の失敗など)。ヘッダー無しで送る(認証不要の API は成功する)が、
      // 認証必須の API が返す 401 でログアウトされないよう印を付けておく(上の onResponseError で見る)
      request.tokenUnavailable = true
      // 再試行したうえで失敗したとき(クールダウン内の最初の 1 回)だけ Sentry に送る。
      // 同時に走ったリクエストは 1 回の取得結果を共有するので、通知は結果ごとに 1 回だけ(最初のリクエストの情報を付ける)
      if (result.retried && !result.reported) {
        result.reported = true
        shouldReport('unavailable') &&
          reportTokenUnavailable({
            sentry,
            error: result.error,
            path: currentPath(),
            method: request.method,
            url: request.url
          })
      }
    }
    // session-lost: ログインしていない / セッションが無効。ヘッダー無しで送り、BE の 401 を受けて
    // 上の onResponseError でサインアウト → ログイン画面へ(従来どおりの流れ)
    return request
  })
}
